import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { retryFailedQueries } from '@/components/retryFailedQueries';
import { usePermissions } from '@/contexts/auth';
import { useDemoMode } from '@/contexts/demo/DemoContext';
import type {
    LocationAverages,
    LocationType,
    OverviewCage,
    OverviewLocation,
} from '@/domain/client';
import { useGetClientOverview } from '@/domain/client';
import { useGetLocationLicenses } from '@/domain/licenses';
import {
    type DailyCageWeight,
    useGetLocationWeightStatus,
    useMarkCageHistorical,
} from '@/domain/locations';
import LocationDashboardPageContent from '@/routes/Client/Location/pages/LocationDashboard/LocationDashboardPageContent';
import { applyDemoMaskToOverviewLocation } from '@/utils/demoMasking';

interface LocationDashboardContainerProps {
    clientId: number;
    locationId: number;
}

export default function LocationDashboardContainer(props: LocationDashboardContainerProps) {
    const { clientId, locationId } = props;

    const { isOptoScaleAdmin } = usePermissions();
    const routerLocation = useLocation();
    const { isDemoMode } = useDemoMode();
    const overviewQuery = useGetClientOverview(clientId);
    const { isLoading, data: clientOverview, error, refetch } = overviewQuery;
    const { t } = useTranslation();

    const internalPage = routerLocation.pathname.startsWith('/internal');

    const { mutateAsync: markHistorical } = useMarkCageHistorical({
        onSettled: () => refetch(),
    });

    const weightStatusQuery = useGetLocationWeightStatus(locationId);
    const dailyCageWeights = weightStatusQuery.data;

    const { data: locationLicenses } = useGetLocationLicenses(clientId);

    const markHistoricalWrapper = async (cageId: number, historical: boolean) => {
        await markHistorical({ cageId, historical });
        await refetch();
    };

    const rawLocation = clientOverview?.locations?.find((x) => x.locationId === locationId) as
        | OverviewLocation
        | undefined;

    const displayLocation = useMemo(() => {
        if (!rawLocation || !isDemoMode || internalPage) {
            return rawLocation;
        }
        return applyDemoMaskToOverviewLocation(rawLocation, t);
    }, [rawLocation, isDemoMode, internalPage, t]);

    const weightStatusRows = useMemo<DailyCageWeight[]>(() => {
        const cages = displayLocation?.cages;
        const dailyRows = dailyCageWeights;
        if (!dailyRows?.length || !cages?.length) {
            return [];
        }
        const result: DailyCageWeight[] = [];
        for (const row of dailyRows) {
            const cage = cages.find((c) => c.cageId === row.cageId);
            if (cage?.cageData.weight?.livingWeight) {
                result.push({ ...row, estimatedWeight: cage.cageData.weight.livingWeight });
            }
        }
        return result;
    }, [dailyCageWeights, displayLocation]);

    return (
        <LocationDashboardPageContent
            clientId={clientId}
            activeCages={displayLocation?.activeCages ?? ([] as OverviewCage[])}
            inactiveCages={displayLocation?.inactiveCages ?? ([] as OverviewCage[])}
            emptyCages={displayLocation?.emptyCages ?? ([] as OverviewCage[])}
            historicalCages={displayLocation?.historicalCages ?? ([] as OverviewCage[])}
            closedFishGroups={displayLocation?.closedFishGroups}
            locationId={locationId}
            locationName={displayLocation?.locationName ?? ''}
            locationType={displayLocation?.locationType as LocationType}
            averages={displayLocation?.locationAverages ?? ({} as LocationAverages)}
            locationLicense={locationLicenses?.find((x) => x.locationId === locationId)}
            weightStatus={{
                rows: weightStatusRows,
                error: weightStatusQuery.error,
                ...retryFailedQueries(weightStatusQuery),
            }}
            markHistorical={markHistoricalWrapper}
            hasAccessToMarkHistorical={isOptoScaleAdmin}
            isOptoScaleAdmin={isOptoScaleAdmin}
            containerLoading={isLoading}
            containerError={error}
            containerRetry={retryFailedQueries(overviewQuery)}
        />
    );
}
